import { CarriagePictogram } from '@/components/icons/CarriagePictogram';
import { PlacePictogram } from '@/components/icons/PlacePictogram';
import PriceDisplay from '@/components/PriceDisplay';
import { useCheckoutBooking } from '@/hooks/useCheckoutBooking';
import { useSeatSelection } from '@/hooks/useSeatSelection';
import type { PassengerSeat } from '@/hooks/useSeatSelectionLegs';
import { useSeatSelectionLegs } from '@/hooks/useSeatSelectionLegs';
import { TransText } from '@/i18n/trans/text';
import {
  findBookingBookedTripFeesByFeeRefs,
  findBookingBookedTripReservationByReservationId,
} from '@/utils/booking';
import { cn } from '@/utils/cn';
import { getPassengerName } from '@/utils/passenger';
import { Radio, Typography } from '@packages/webshop-design-system';
import type { FC } from 'react';

interface PassengerSelectionItemProps {
  passenger: PassengerSeat;
  isSelected: boolean;
  onSelect: () => void;
  passengerIndex: number;
  isDisabled?: boolean;
}

export const PassengerSelectionItem: FC<PassengerSelectionItemProps> = ({
  passenger,
  isSelected,
  onSelect,
  passengerIndex,
  isDisabled = false,
}) => {
  const { selectedLegId } = useSeatSelection();
  const { legs } = useSeatSelectionLegs();
  const selectedLeg = legs.find((leg) => leg.id === selectedLegId);
  const booking = useCheckoutBooking();

  const currentPassenger = selectedLeg?.passengers.find(
    (p) => p.reservationId === passenger.reservationId
  );
  const originalSeat = passenger.reservedPlace?.placeNumber?.join(', ');
  const currentSeat = currentPassenger?.reservedPlace?.placeNumber?.join(', ');
  const originalCoach = passenger.reservedPlace?.coachNumber;
  const currentCoach = currentPassenger?.reservedPlace?.coachNumber;

  const matchingReservation = currentPassenger?.reservationId
    ? findBookingBookedTripReservationByReservationId(
        booking,
        currentPassenger.reservationId
      )
    : undefined;
  const seatChangeFees = findBookingBookedTripFeesByFeeRefs(
    booking,
    matchingReservation?.feeRefs
  );

  const handleClick = () => {
    if (!isDisabled) {
      onSelect();
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md border border-neutral-light p-2 hover:border-primary hover:bg-primary-light',
        {
          'border-primary bg-primary-light': isSelected,
          'cursor-not-allowed opacity-50': isDisabled,
        }
      )}
      data-testid="passenger-selection-item"
    >
      <div className="flex min-w-0 flex-1">
        <Radio value={passenger.reservationId ?? ''} className="mr-3" />
        <div className="flex w-fit flex-col overflow-hidden">
          <div className="flex items-center gap-1">
            <Typography
              variant="body2-bold"
              className="w-full truncate text-dark"
            >
              {getPassengerName(passenger, passengerIndex)}
            </Typography>
          </div>
          {!!seatChangeFees?.length && (
            <div className="flex items-center">
              <Typography variant="label" className="text-neutral">
                <TransText i18nKey="changedSeat" />:
              </Typography>
              <Typography variant="label" className="text-primary">
                <PriceDisplay
                  amount={seatChangeFees.reduce(
                    (acc, cur) => acc + (cur.price?.amount || 0),
                    0
                  )}
                />
              </Typography>
            </div>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {selectedLeg?.ptMode?.code !== 'BUS' && (
          <Typography
            variant="body2-bold"
            className="flex items-center gap-1 text-neutral"
          >
            <CarriagePictogram
              width={12}
              height={12}
              className="text-neutral"
            />
            {seatChangeFees?.length ? currentCoach : originalCoach}
          </Typography>
        )}

        <Typography
          variant="body2-bold"
          className="flex items-center gap-1 text-neutral"
        >
          <PlacePictogram
            placeIcon={passenger.placeIcon}
            height={12}
            width={12}
          />
          {seatChangeFees?.length ? currentSeat : originalSeat}
        </Typography>
      </div>
    </div>
  );
};
